import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import ReactSelect from "react-select";
import { AlertCircle, Package } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getEditProcess,
  getIsProcessModalOpen,
  toggleProcessModal,
} from "../../features/processes/processesSlice";
import {
  fetchProcessDefinitions,
  getProcessDefinitions,
} from "../../features/processDefinitions/processDefinitionsSlice";
import { IFormInput } from "../../types/process";
import { ProcessModePolicy } from "../../types/processDefinition";
import { localizedProcessName } from "../../lib/utils";
import { t } from "../../locales";
import { filterSelectStyles } from "../../static/styles";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

const modalInputClass = (hasError: boolean) =>
  `h-12 text-base border-2 rounded-lg transition-all duration-200 ${
    hasError
      ? "border-red-500 focus:border-red-500 focus:shadow-focus-error"
      : "border-gray-200 focus:border-[#113F67] focus:shadow-focus-brand"
  }`;

const modalTextareaClass = (hasError: boolean) =>
  `text-base border-2 rounded-lg transition-all duration-200 ${
    hasError
      ? "border-red-500 focus:border-red-500 focus:shadow-focus-error"
      : "border-gray-200 focus:border-[#113F67] focus:shadow-focus-brand"
  }`;

const modalSelectStyles: any = {
  ...filterSelectStyles,
  control: (base: any, state: any) => ({
    ...(typeof filterSelectStyles.control === "function"
      ? (filterSelectStyles.control as any)(base, state)
      : base),
    borderRadius: "0.5rem",
    fontSize: "1rem",
  }),
};

const modalSelectErrorStyles: any = {
  ...modalSelectStyles,
  control: (base: any, state: any) => ({
    ...(typeof modalSelectStyles.control === "function"
      ? modalSelectStyles.control(base, state)
      : base),
    borderColor: "#ef4444",
    boxShadow: state.isFocused ? "0 0 0 2px #fff, 0 0 0 4px #ef4444" : "none",
    "&:hover": { borderColor: "#ef4444" },
  }),
};

type Props = {
  handleSaveProcess: (data: IFormInput) => void;
};

const ProcessFormModal = ({ handleSaveProcess }: Props) => {
  const dispatch = useAppDispatch();
  const editProcess = useAppSelector(getEditProcess);
  const isProcessModalOpen = useAppSelector(getIsProcessModalOpen);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const processDefinitions = useAppSelector(getProcessDefinitions);

  useEffect(() => {
    dispatch(fetchProcessDefinitions({ all: true }));
  }, [dispatch]);

  const definitionOptions = processDefinitions
    .filter((d) => d.is_active)
    .map((d) => ({ value: d.id, label: localizedProcessName(d) }));

  const modePolicyOptions: { value: ProcessModePolicy; label: string }[] = [
    { value: "in_house", label: t.processes.modePolicyInHouse },
    { value: "outsource", label: t.processes.modePolicyOutsource },
    { value: "both", label: t.processes.modePolicyBoth },
  ];

  const nextProcessMandatoryOptions: { value: boolean | ""; label: string }[] = [
    { value: "", label: t.processes.nextProcessMandatoryInherit },
    { value: true, label: t.processes.nextProcessMandatoryYes },
    { value: false, label: t.processes.nextProcessMandatoryNo },
  ];

  const {
    handleSubmit,
    register,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<IFormInput>({
    defaultValues: {
      name: "",
      process_definition_id: "",
      mode_policy: "",
      next_process_definition_ids: [],
      next_process_mandatory: "",
      description: "",
    },
  });

  // A sub-process can't forward to its own parent process.
  const selectedDefinitionId = watch("process_definition_id");
  const nextProcessOptions = processDefinitions
    .filter((d) => d.is_active && d.id !== selectedDefinitionId)
    .map((d) => ({ value: d.id, label: localizedProcessName(d) }));

  const handleOnSubmit: SubmitHandler<IFormInput> = async (data: any) => {
    setIsLoading(true);
    await handleSaveProcess(data);
    handleCloseFormModal();
    setIsLoading(false);
  };

  const handleCloseFormModal = () => {
    reset();
    dispatch(toggleProcessModal(false));
  };

  useEffect(() => {
    if (editProcess && editProcess.id) {
      reset({
        name: editProcess.name,
        process_definition_id: editProcess.process_definition_id ?? "",
        mode_policy: editProcess.mode_policy ?? "",
        next_process_definition_ids: editProcess.next_process_definition_ids || [],
        next_process_mandatory: editProcess.next_process_mandatory ?? "",
        description: editProcess.description,
      });
    } else {
      reset({
        name: "",
        process_definition_id: "",
        mode_policy: "",
        next_process_definition_ids: [],
        next_process_mandatory: "",
        description: "",
      });
    }
  }, [editProcess, isProcessModalOpen]);

  return (
    <Dialog
      open={isProcessModalOpen}
      onOpenChange={(open) => {
        if (!open) handleCloseFormModal();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#113F67]">
            {t.processes.formTitle}
          </DialogTitle>
          <DialogDescription>
            {editProcess ? `Editing: ${editProcess.name}` : t.processes.addNewProcess}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleOnSubmit)} className="space-y-4 flex-1 flex flex-col sm:block pb-16 sm:pb-0 sm:space-y-5">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-semibold text-[#113F67]">{t.processes.nameLabel} <span className="text-red-500">*</span></Label>
            <Input
              id="name"
              placeholder={t.processes.namePlaceholder}
              {...register("name", { required: true })}
              className={modalInputClass(!!errors.name)}
            />
            {errors.name && errors.name.type === "required" && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />{t.processes.nameRequired}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="process_definition_id" className="text-sm font-semibold text-[#113F67]">
              {t.processes.typeLabel} <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <Package className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
              <Controller
                name="process_definition_id"
                control={control}
                rules={{ required: t.processes.typeRequired }}
                render={({ field }) => (
                  <ReactSelect
                    inputId="process_definition_id"
                    styles={errors.process_definition_id ? modalSelectErrorStyles : modalSelectStyles}
                    options={definitionOptions}
                    value={definitionOptions.find((o) => o.value === field.value) || null}
                    onChange={(opt: any) => field.onChange(opt?.value ?? "")}
                    placeholder={t.processes.typePlaceholder}
                    isClearable={false}
                  />
                )}
              />
            </div>
            {errors.process_definition_id && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />{t.processes.typeRequired}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="mode_policy" className="text-sm font-semibold text-[#113F67]">
              {t.processes.modePolicyLabel}
            </Label>
            <Controller
              name="mode_policy"
              control={control}
              render={({ field }) => (
                <ReactSelect
                  inputId="mode_policy"
                  styles={modalSelectStyles}
                  options={modePolicyOptions}
                  value={modePolicyOptions.find((o) => o.value === field.value) || null}
                  onChange={(opt: any) => field.onChange(opt?.value ?? "")}
                  placeholder={t.processes.modePolicyPlaceholder}
                  isClearable
                />
              )}
            />
            <p className="text-xs text-gray-500">{t.processes.modePolicyInheritHint}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="next_process_definition_ids" className="text-sm font-semibold text-[#113F67]">
              {t.processes.nextProcessLabel}
            </Label>
            <Controller
              name="next_process_definition_ids"
              control={control}
              render={({ field }) => (
                <ReactSelect
                  inputId="next_process_definition_ids"
                  isMulti
                  styles={modalSelectStyles}
                  options={nextProcessOptions}
                  value={nextProcessOptions.filter((o) => field.value?.includes(o.value))}
                  onChange={(selected: any) => field.onChange(selected ? selected.map((o: any) => o.value) : [])}
                  placeholder={t.processes.nextProcessPlaceholder}
                />
              )}
            />
            <p className="text-xs text-gray-500">{t.processes.nextProcessInheritHint}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="next_process_mandatory" className="text-sm font-semibold text-[#113F67]">
              {t.processes.nextProcessMandatoryLabel}
            </Label>
            <Controller
              name="next_process_mandatory"
              control={control}
              render={({ field }) => (
                <ReactSelect
                  inputId="next_process_mandatory"
                  styles={modalSelectStyles}
                  options={nextProcessMandatoryOptions}
                  value={nextProcessMandatoryOptions.find((o) => o.value === field.value) || null}
                  onChange={(opt: any) => field.onChange(opt?.value ?? "")}
                  placeholder={t.processes.nextProcessMandatoryPlaceholder}
                  isClearable={false}
                />
              )}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-semibold text-[#113F67]">{t.processes.descriptionLabel}</Label>
            <Textarea
              id="description"
              rows={3}
              placeholder={t.processes.descriptionPlaceholder}
              {...register("description", { required: false })}
              className={modalTextareaClass(false)}
            />
          </div>

          <div className="fixed bottom-0 left-0 right-0 flex gap-3 p-4 justify-end border-t bg-white z-10 sm:static sm:p-0 sm:pt-2 sm:z-auto sm:border-t sm:bg-transparent">
            <Button
              type="button"
              variant="outline"
              className="h-11 px-5 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67]"
              onClick={handleCloseFormModal}
              disabled={isLoading}
            >
              {t.processes.cancelButton}
            </Button>
            <Button
              type="submit"
              className="h-11 px-5 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67]"
              disabled={isLoading}
            >
              {isLoading ? t.processes.saving : t.processes.saveButton}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProcessFormModal;
